import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * useOptimisticVote (V1.2 - Industrial Odometer)
 *
 * Logic:
 * 1. User votes -> UI updates instantly (Optimistic +5% nudge).
 * 2. Component calls triggerOptimistic(choiceId) to start the nudge.
 * 3. Component listens for 'poll_update' from WebSocket via props -> Overrides with official data.
 *
 * The `isNudging` flag is the guard that prevents these two data sources from fighting each
 * other: while true (for 5s after triggerOptimistic, or until server data arrives — whichever
 * happens first via handleServerSync's early-return), incoming `initialResults` prop changes
 * are ignored (see the sync useEffect below). A `poll_update` socket event that arrives while
 * a nudge is mid-flight is NOT dropped though — handleServerSync() stashes the latest one in
 * `pendingServerDataRef`, and it's applied as soon as the nudge window ends (either via the
 * timeout in triggerOptimistic, or immediately if handleServerSync itself is called again
 * after isNudging has cleared), so the UI never permanently shows a stale optimistic guess
 * once fresher official data has actually arrived.
*/
export function useOptimisticVote(pollId, initialResults) {
    const [optimisticResults, setOptimisticResults] = useState(initialResults);
    const [isNudging, setIsNudging] = useState(false);
    const pendingServerDataRef = useRef(null);

    // Update optimistic state if initialResults (from props) changes (Sync from DB/Socket)
    useEffect(() => {
        if (!isNudging) {
            setOptimisticResults(initialResults);
        }
    }, [initialResults, isNudging]);

    /**
     * triggerOptimistic
     * 
     * Manually kicks off the bar slide for a specific choice.
     */
    const triggerOptimistic = useCallback((choiceId) => {
        setIsNudging(true);
        console.log(`[Odometer] 🥊 Nudging choice ${choiceId} for poll ${pollId}`);

        setOptimisticResults(prev => {
            const current = prev || {
                pollId,
                rawCount: 0,
                weightedPercentages: {}
            };

            const newWeights = { ...current.weightedPercentages };
            
            // Industrial Nudge Logic:
            // Only jump to 100% if we are ABSOLUTELY sure this is the first vote (rawCount 0).
            // If rawCount is missing or we are in a 'blind' reveal, don't guess 100%.
            const hasExistingData = Object.keys(newWeights).length > 0;
            
            if (!hasExistingData) {
                // If we don't have weights, don't assume 100% unless we know it's the first vote
                // For now, let's just initialize it with a 'placeholder' but keep it at 0 
                // so the bars don't flicker to 100% incorrectly.
                newWeights[choiceId] = 0; 
            } else {
                newWeights[choiceId] = (newWeights[choiceId] || 0) + 5; // Subtle +5% nudge
                // Normalize slightly so it doesn't exceed 100
                const sum = Object.values(newWeights).reduce((a, b) => a + b, 0);
                if (sum > 100) {
                    Object.keys(newWeights).forEach(k => newWeights[k] = (newWeights[k] / sum) * 100);
                }
            }

            return {
                ...current,
                rawCount: (current.rawCount || 0) + 1,
                weightedPercentages: newWeights
            };
        });

        // After a few seconds, release the lock so official WebSocket data can take over. If a
        // 'poll_update' arrived while the nudge was active, it was queued (see
        // handleServerSync below) instead of dropped — apply the freshest one now.
        setTimeout(() => {
            setIsNudging(false);
            if (pendingServerDataRef.current) {
                const officialData = pendingServerDataRef.current;
                pendingServerDataRef.current = null;
                setOptimisticResults({
                    pollId: officialData.pollId,
                    rawCount: officialData.totalVoters,
                    weightedPercentages: officialData.weightedPercentages
                });
            }
        }, 5000);
    }, [pollId]);

    /**
     * handleServerSync
     *
     * Snaps the bar to the official mathematical truth from WebSockets. While a local
     * optimistic nudge is active, the update isn't applied immediately (that would fight the
     * in-flight animation) — instead the latest payload is queued in `pendingServerDataRef`
     * and applied automatically once the nudge window ends (see triggerOptimistic's timeout
     * above), so fresher official data is never silently lost.
     */
    const handleServerSync = useCallback((officialData) => {
        if (isNudging) {
            pendingServerDataRef.current = officialData; // keep only the most recent
            return;
        }

        setOptimisticResults({
            pollId: officialData.pollId,
            rawCount: officialData.totalVoters,
            weightedPercentages: officialData.weightedPercentages
        });
    }, [isNudging]);

    return {
        results: optimisticResults,
        triggerOptimistic,
        handleServerSync
    };
}
